import {
    faCakeCandles,
    faLaptop,
    faStar,
    faSyringe,
    faBadgeDollar,
    faBellConcierge
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { LoyaltyConfig, LoyaltyConfigUpdate, PurchasePointsBasis } from '@spike/loyalty-model';
import { LoyaltyEditFieldConfig } from '../UI';
import { formatInt, toIntInputValue, toIntValue } from '../utils';

export interface LoyaltyRule {
    icon: IconDefinition;
    key: string;
    title: string;
    description: string;
    editDescription: string;
    enabledField: keyof LoyaltyConfig;
    fields: Array<LoyaltyEditFieldConfig>;
}

export const buildEarningRules = (config: LoyaltyConfig): Array<LoyaltyRule> => [
    {
        key: 'purchase',
        icon: faBadgeDollar,
        title: 'Make a Purchase',
        description: `${formatInt(config.pointsPerDollarSpent)} points for every $1 spent`,
        editDescription: 'Set the number of points clients earn for every $1 they spend.',
        enabledField: 'earnPurchaseEnabled',
        fields: [
            {
                key: 'pointsPerDollarSpent',
                label: 'For Every $1 Spent Clients Earn',
                value: toIntInputValue(config.pointsPerDollarSpent),
                suffix: 'Points',
                fullWidth: true
            },
            {
                key: 'purchasePointsBasis',
                label: 'Calculation Method',
                subtitle: 'Choose whether to calculate points before or after applying discounts.',
                value: config.purchasePointsBasis,
                type: 'radio',
                fullWidth: true,
                radioOptions: [
                    { value: PurchasePointsBasis.BeforeDiscount, inlineLabel: 'Before Discounts' },
                    { value: PurchasePointsBasis.AfterDiscount, inlineLabel: 'After Discounts' }
                ]
            }
        ]
    },
    {
        key: 'review',
        icon: faStar,
        title: 'Leave a Review',
        description: `${formatInt(config.pointsPerReview)} points for leaving a review`,
        editDescription: 'Set how many points clients earn when they leave a Cuddles review.',
        enabledField: 'earnReviewEnabled',
        fields: [
            {
                key: 'pointsPerReview',
                label: 'For Every Review Clients Earn',
                value: toIntInputValue(config.pointsPerReview),
                suffix: 'Points'
            }
        ]
    },
    {
        key: 'online-booking',
        icon: faLaptop,
        title: 'Online Booking',
        description: `${formatInt(config.pointsPerOnlineBooking)} points for booking online`,
        editDescription: 'Set the points clients earn each time they complete an online booking.',
        enabledField: 'earnOnlineBookingEnabled',
        fields: [
            {
                key: 'pointsPerOnlineBooking',
                label: 'For Every Booking Clients Earn',
                value: toIntInputValue(config.pointsPerOnlineBooking),
                suffix: 'Points'
            }
        ]
    },
    {
        key: 'total-bookings',
        icon: faBellConcierge,
        title: 'Total Bookings Reached',
        description: `${formatInt(config.bookingsMilestonePoints)} points for completing ${formatInt(
            config.bookingsMilestoneCount
        )} bookings`,
        editDescription: 'Set the number of points clients earn for every set number of bookings completed.',
        enabledField: 'earnBookingsMilestoneEnabled',
        fields: [
            {
                key: 'bookingsMilestonePoints',
                label: 'Clients Earn',
                value: toIntInputValue(config.bookingsMilestonePoints),
                suffix: 'Points'
            },
            {
                key: 'bookingsMilestoneCount',
                label: 'For Every',
                value: toIntInputValue(config.bookingsMilestoneCount),
                suffix: 'Bookings'
            }
        ]
    },
    {
        key: 'pet-birthday',
        icon: faCakeCandles,
        title: 'Pet Birthday',
        description: `${formatInt(config.pointsPerPetBirthday)} points for pet birthday`,
        editDescription: "Set the number of points clients receive on their pet's birthday.",
        enabledField: 'earnPetBirthdayEnabled',
        fields: [
            {
                key: 'pointsPerPetBirthday',
                label: 'For Every Pet Birthday',
                value: toIntInputValue(config.pointsPerPetBirthday),
                suffix: 'Points'
            }
        ]
    },
    {
        key: 'vaccines',
        icon: faSyringe,
        title: 'Vaccines',
        description: `${formatInt(config.pointsPerVaccineUpdate)} points for update vaccines by client`,
        editDescription: 'Set how many points clients receive for updating vaccine information.',
        enabledField: 'earnVaccineUpdateEnabled',
        fields: [
            {
                key: 'pointsPerVaccineUpdate',
                label: 'For Every Vaccine Updated',
                value: toIntInputValue(config.pointsPerVaccineUpdate),
                suffix: 'Points'
            }
        ]
    }
];

export const buildLoyaltyPointsFields = (config: LoyaltyConfig): Array<LoyaltyEditFieldConfig> => [
    /*  {
        key: 'pointsExpireMonths',
        label: 'Points expire after',
        value: toIntInputValue(config.pointsExpireMonths),
        suffix: 'months',
        type: 'select',
        fullWidth: true,
        options: [
            { label: '12 months', value: '12' },
            { label: '24 months', value: '24' },
            { label: 'Never', value: '0' }
        ]
    }, */
    {
        key: 'holdPeriodDays',
        label: 'Points Hold Period',
        value: toIntInputValue(config.holdPeriodDays),
        suffix: 'Days'
    },
    {
        key: 'pointsPerDollarValue',
        label: 'Points per dollar',
        value: toIntInputValue(config.pointsPerDollarValue),
        suffix: 'Points'
    }
];

const NUMERIC_CONFIG_KEYS = new Set<keyof LoyaltyConfig>([
    'holdPeriodDays',
    'pointsExpireMonths',
    'pointsPerDollarValue',
    'pointsPerDollarSpent',
    'pointsPerReview',
    'pointsPerOnlineBooking',
    'bookingsMilestoneCount',
    'bookingsMilestonePoints',
    'pointsPerPetBirthday',
    'pointsPerVaccineUpdate'
]);

export const buildConfigUpdate = (changes: Record<string, string>): LoyaltyConfigUpdate => {
    const update: LoyaltyConfigUpdate = {};

    Object.keys(changes).forEach(key => {
        const configKey = key as keyof LoyaltyConfig;
        const value = changes[key];

        if (NUMERIC_CONFIG_KEYS.has(configKey)) {
            (update as Record<string, unknown>)[configKey] = toIntValue(value);
        } else if (configKey === 'purchasePointsBasis') {
            update.purchasePointsBasis = value as PurchasePointsBasis;
        }
    });

    return update;
};

export type EditTarget = 'loyalty-points' | string | null;
